import * as Sentry from "@sentry/browser";
import { SENSITIVE_SELECTOR } from "../replayPrivacy";
import { blockedException } from "./logDecisionObject";
import { SENTRY_RELEASE, SENTRY_REPLAY } from "gatsby-env-variables";
import { isViewAsTab } from "@helpers/viewAs/viewAsSession";
import { isViewAsEndedError } from "@helpers/viewAs/viewAsRefusal";

const FAILED_ANALYTICS_URL_PREFIXES = [
  "https://api-eu.mixpanel.com/track/",
  "https://www.google-analytics.com/g/collect",
  "https://cdp-eu.customer.io/v1/t",
];

const REMOTE_CONFIG_OBJECT_STORE_NAME = "app_namespace_store";
const REMOTE_CONFIG_OBJECT_STORE_ERROR = "not a known object store name";

interface StackFrameSubset {
  abs_path?: unknown;
  filename?: unknown;
  module?: unknown;
}

interface SentryEventSubset {
  breadcrumbs?: Array<{
    category?: string;
    data?: {
      url?: unknown;
      status_code?: unknown;
    };
  }>;
  exception?: {
    values?: Array<{
      type?: string;
      value?: unknown;
      stacktrace?: {
        frames?: StackFrameSubset[];
      };
    }>;
  };
}

const getErrorMessage = (error: unknown): string | undefined =>
  typeof error === "string"
    ? error
    : typeof (error as { message?: unknown } | undefined)?.message === "string"
    ? ((error as { message?: string }).message as string)
    : undefined;

const hasRemoteConfigStackFrame = (event: SentryEventSubset): boolean =>
  event.exception?.values?.some((exception) =>
    exception.stacktrace?.frames?.some((frame) =>
      [frame.abs_path, frame.filename, frame.module].some(
        (value) =>
          typeof value === "string" && /@?firebase\/remote-config/i.test(value)
      )
    )
  ) ?? false;

const isRemoteConfigObjectStoreNotFound = (
  event: SentryEventSubset,
  error: unknown,
  message?: string,
  eventValue?: string
): boolean => {
  const errorName =
    typeof (error as { name?: unknown } | undefined)?.name === "string"
      ? ((error as { name?: string }).name as string)
      : "";
  const combinedMessage = [errorName, message, eventValue]
    .filter((value): value is string => typeof value === "string")
    .join(" ");

  return (
    combinedMessage.includes(REMOTE_CONFIG_OBJECT_STORE_NAME) &&
    combinedMessage.toLowerCase().includes(REMOTE_CONFIG_OBJECT_STORE_ERROR) &&
    (errorName === "NotFoundError" || hasRemoteConfigStackFrame(event))
  );
};

const JSON_RPC_ERROR_CODE_MIN = -32768;
const JSON_RPC_ERROR_CODE_MAX = -32000;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value) as unknown;

  return (
    prototype === null ||
    prototype === Object.prototype ||
    Object.getPrototypeOf(prototype) === null
  );
};

const isInjectedJsonRpcRejection = (error: unknown): boolean => {
  if (!isPlainObject(error)) {
    return false;
  }

  const { code, message } = error;

  return (
    typeof code === "number" &&
    Number.isInteger(code) &&
    code >= JSON_RPC_ERROR_CODE_MIN &&
    code <= JSON_RPC_ERROR_CODE_MAX &&
    typeof message === "string"
  );
};

const isStacklessNotReadableIoFailure = (
  event: SentryEventSubset,
  message?: string
): boolean => {
  if (message !== "The I/O read operation failed.") {
    return false;
  }

  const exceptionValues = event.exception?.values ?? [];
  const exceptionType = exceptionValues[0]?.type;
  const hasStackFrames = exceptionValues.some(
    (exception) => (exception.stacktrace?.frames?.length ?? 0) > 0
  );

  if (exceptionType !== "NotReadableError" || hasStackFrames) {
    return false;
  }

  return (
    event.breadcrumbs?.some((breadcrumb) => {
      const url = breadcrumb.data?.url;
      const statusCode = breadcrumb.data?.status_code;

      return (
        (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") &&
        typeof url === "string" &&
        statusCode === 0 &&
        FAILED_ANALYTICS_URL_PREFIXES.some((prefix) => url.startsWith(prefix))
      );
    }) ?? false
  );
};

export const initSentry = (dsn: string): void => {
  const isDev = process.env.NODE_ENV === "development";
  const shouldEnableReplay =
    SENTRY_REPLAY === "true" && !isDev && !isViewAsTab();

  const replayConfiguration = shouldEnableReplay
    ? {
        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        replaysSessionSampleRate: 0.1,

        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
      }
    : {};

  const integrations: any = [Sentry.browserTracingIntegration({})];

  if (shouldEnableReplay) {
    integrations.push(
      Sentry.replayIntegration({
        networkDetailAllowUrls: [/^https:\/\/api\.cur8\.capital/],
        // Unmasked for usable triage; mark sensitive elements data-sensitive.
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        mask: [SENSITIVE_SELECTOR],
      })
    );
  }

  Sentry.init({
    dsn,
    // The deploy workflow injects the deployed commit SHA at build time and
    // uploads source maps under the same value, so every event carries a
    // release that maps back to the exact artefacts that produced it.
    release: SENTRY_RELEASE || undefined,
    ...replayConfiguration,
    integrations,
    tracesSampleRate: 1.0,
    ignoreErrors: [
      // Random plugins/extensions
      "top.GLOBALS",
      /Illegal invocation/gi,
      // Sentry/replay throws it when on Windows for some reason
      /Cannot read properties of undefined (reading 'contains')/gi,
      // Sentry Replay + cross-origin iframe (e.g., Turnstile) instrumentation noise
      /Cannot read properties of undefined \(reading 'prototype'\)/gi,
      // External script errors (Intercom, analytics, browser extensions, etc.)
      /Object Not Found Matching Id:\d+/gi,
      /MethodName:(simulateEvent|update)/gi,
      // Cross-origin OAuth / iframe access
      /Failed to set a named property 'blur' on 'Window'/gi,
      // Cloudflare Turnstile script parse errors (Safari/content blockers)
      /Unterminated regular expression literal/gi,
      // Firebase Remote Config IndexedDB failures (iOS/WebKit quirk, no functional impact)
      /remoteconfig\/storage-/gi,
      // Firebase SDK internal feature detection errors (not actionable)
      /feature named .* was not found/gi,
      // Browser extension errors (WebExtensions runtime.sendMessage API)
      /Invalid call to runtime\.sendMessage\(\)/gi,
      // Third-party / browser-extension injected scripts calling methods that
      // don't exist in the current environment (e.g. "Error invoking postEvent:
      // Method not found"). Stack traces show <anonymous> — not application code.
      /Error invoking .+: Method not found/gi,
      // Safari throws "TypeError: Load failed" for fetch/dynamic-import failures
      // (navigation during in-flight requests, network interruptions, ad blockers).
      // This is Safari's equivalent of "Failed to fetch" in Chrome.
      /^Load failed$/,
      // Firefox throws "TypeError: NetworkError when attempting to fetch resource."
      // when a fetch is blocked by Enhanced Tracking Protection or network failure.
      // Arrives as an unhandled rejection with no stack trace, so stack-trace-based
      // filters cannot catch it.
      /^NetworkError when attempting to fetch resource\.?$/,
      // Browsers in privacy mode or with site-data blocked deny localStorage access,
      // throwing a SecurityError that is environmental and not actionable.
      /Failed to read the 'localStorage' property/i,
      // Meta in-app-browser WKWebView bridge access, third-party injected, not actionable.
      /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers'\)/i,
    ],

    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Cloudflare Turnstile widget script
      /\/turnstile\/v0\/.*\/api\.js/i,
      // Hotjar analytics scripts can throw browser-specific localStorage errors.
      /script\.hotjar\.com\/modules\..*\.js/i,
      // Plaid Link SDK — receives postMessage events not intended for it (e.g. from
      // browser extensions or other iframes), causing benign "Cannot read properties
      // of undefined (reading 'action')" errors entirely within Plaid's own code.
      /\/link\/v\d+\/.*\/link-initialize\.js/i,
    ],

    beforeSend(event, hint) {
      const error = hint.originalException as any;

      if (error === null || error === undefined) {
        return null;
      }

      const message = getErrorMessage(error);
      const axiosStatus = (
        error as { isAxiosError?: unknown; response?: unknown }
      )?.isAxiosError
        ? (error as { response?: { status?: unknown } })?.response?.status
        : undefined;
      const axiosCode = error?.isAxiosError
        ? (error?.code as unknown)
        : undefined;

      // Firebase Remote Config IndexedDB errors surface as unhandled promise
      // rejections on iOS Safari when the browser evicts or deletes the DB.
      // These are non-actionable noise — the app falls back to safe defaults.
      if (
        error?.code?.startsWith?.("remoteconfig/storage-") ||
        (typeof message === "string" && /remoteconfig\/storage-/i.test(message))
      ) {
        return null;
      }

      // Firebase Remote Config's onupgradeneeded callback throws a raw TypeError
      // when IndexedDB hands it an undefined database (Safari private mode or IDB
      // unavailable). The callback runs outside the Firebase promise chain so the
      // SDK cannot catch it — it escapes to Sentry's global handler. The app
      // already falls back to defaults, so there is no functional impact.
      if (
        hasRemoteConfigStackFrame(event) &&
        typeof message === "string" &&
        message.includes("createObjectStore")
      ) {
        return null;
      }

      // Drop CustomEvent promise rejections (noise from third-party scripts/iframes).
      // These have no stacktrace and are not actionable.
      if (error instanceof CustomEvent) {
        return null;
      }

      // Also filter by event value pattern for cases where hint.originalException
      // might not be the CustomEvent instance directly.
      const errorValue = event.exception?.values?.[0]?.value || "";
      const normalizedErrorValue =
        typeof errorValue === "string" ? errorValue : undefined;

      if (
        isRemoteConfigObjectStoreNotFound(
          event,
          error,
          message,
          normalizedErrorValue
        )
      ) {
        return null;
      }

      if (
        isStacklessNotReadableIoFailure(event, message ?? normalizedErrorValue)
      ) {
        return null;
      }

      if (
        typeof errorValue === "string" &&
        /CustomEvent.*type=unhandledrejection/i.test(errorValue)
      ) {
        return null;
      }

      // It filters the "Non-Error promise rejection captured with value: Timeout" error report
      if (error === "Timeout") return null;

      // AbortError (DOMException, code 20 = ABORT_ERR) reaches Sentry via
      // `onunhandledrejection` when an in-flight fetch is cancelled by the
      // browser. In-app browsers on iOS (Google Search, Facebook, Instagram,
      // etc.) aggressively abort requests on backgrounding, navigation, or
      // tab switch. There is no app code to wrap with .catch() — the abort
      // is initiated by the platform, not by us — so the only place to
      // suppress this noise is here. The early return above already drops
      // events whose hint.originalException is null/undefined, so a name
      // check on the DOMException is sufficient.
      if ((error as { name?: unknown })?.name === "AbortError") {
        return null;
      }

      if (isInjectedJsonRpcRejection(error)) {
        return null;
      }

      if (isViewAsEndedError(error)) {
        return null;
      }

      // Drop expected Axios auth/permission failures (noise)
      if (axiosStatus === 401 || axiosStatus === 403) {
        return null;
      }

      // Drop Axios network errors (typically user connectivity/offline noise).
      // NOTE: Some Sentry-captured Axios errors may not have hint.originalException populated,
      // so we also check the event exception value as a fallback below.
      if (
        axiosCode === "ERR_NETWORK" ||
        axiosCode === "ECONNABORTED" ||
        (error?.isAxiosError && message === "Network Error")
      ) {
        return null;
      }

      // Drop Axios 400 Bad Request errors (typically client validation / user-input issues).
      if (
        axiosStatus === 400 ||
        (typeof errorValue === "string" &&
          /AxiosError:\s*Request failed with status code 400/i.test(errorValue))
      ) {
        return null;
      }

      // Fallback filtering when hint.originalException isn't available.
      if (
        typeof errorValue === "string" &&
        (/AxiosError:\s*Network Error/i.test(errorValue) ||
          /Network Error/i.test(errorValue))
      ) {
        return null;
      }

      if (
        typeof errorValue === "string" &&
        /remoteconfig\/storage-/i.test(errorValue)
      ) {
        return null;
      }

      const externalScriptErrorPatterns = [
        "Object Not Found Matching Id",
        "MethodName:simulateEvent",
        "MethodName:update",
        "Method not found",
      ];

      const isExternalScriptError = externalScriptErrorPatterns.some(
        (pattern) => errorValue.includes(pattern)
      );

      if (isExternalScriptError) return null;

      if (typeof message === "string" && blockedException(message)) {
        return null;
      }

      if (typeof error === "string" && blockedException(error)) {
        return null;
      }

      // When returning null, we tell Sentry to don't log this error
      if (typeof error?.stack === "string" && blockedException(error?.stack)) {
        return null;
      }

      // When returning the event we're telling it to log it.
      return event;
    },
  });
};
